import React, { useState, useEffect } from "react";
import MUIDataTable from "mui-datatables";
import CircularProgress from '@mui/material/CircularProgress';
import { Input } from 'reactstrap';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import { NotificationManager } from 'react-notifications';
import moment from 'moment';
import SearchBar from "../sellerSalesReport/searchbar";
import axios from "axios";
import AppConfig from "constants/config";
import AdminContainer from "containers/AdminContainer";
import Pagination from '@mui/material/Pagination';
import PaginationCount from "../components/PaginationCount";

// Static (non-quarter) columns. `name` is the data key, `label` is the header / CSV column.
const baseColumns = [
  { name: 'product', label: 'Product' },
  { name: 'medicineType', label: 'Medicine Type' },
  { name: 'company', label: 'Company' },
  { name: 'seller', label: 'Seller' },
  { name: 'listingDate', label: 'Listing/Updated Date' },
  {
    name: 'listingType',
    label: 'Listing Type',
    options: {
      customBodyRender: (value) => (
        <span className={`badge text-uppercase text-white ${value === 'Re-Listing' ? 'bg-primary' : 'bg-danger'}`}>{value}</span>
      )
    }
  },
  { name: 'discount', label: 'Deal/Discount Details' },
  { name: 'listedQuantity', label: 'Listed Quantity' },
  {
    name: 'status',
    label: 'Status',
    options: {
      customBodyRender: (value) => (
        <span className={`badge text-uppercase text-white ${value === 'Active' ? 'bg-success' : 'bg-grey'}`}>{value}</span>
      )
    }
  },
];

const ROWS_PER_PAGE = 100

const ProductForPromotion = (props) => {
  const [loader, setLoader] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [report, setReport] = useState([])
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(0)
  const [searchedText, setSearchedText] = useState('')
  const [filterData, setFilterData] = useState('')
  const [seller, setSeller] = useState('')
  const [columns, setColumns] = useState(baseColumns)
  const [snapshotDate, setSnapshotDate] = useState(null)

  const selectedSeller = filterData && filterData.find(s => s._id === seller)
  const sellerSlug = selectedSeller && selectedSeller.company_name
    ? selectedSeller.company_name.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase()
    : 'all-sellers'

  // Server-side paginated fetch. page is 0-based (MUIDataTable convention).
  const getPromotionReport = async (sellerId = seller, pageArg = 0, searchArg = searchedText) => {
    let data = {
      seller_id: sellerId,
      search: searchArg,
      page: pageArg + 1,
      limit: ROWS_PER_PAGE,
    }
    setLoader(true)
    await axios.post(`${AppConfig.baseUrl}reports/getPromotionReport`, data, {
      headers: {
        'Content-Type': 'application/json',
        'token': localStorage.getItem('admin_token')
      }
    }).then(result => {
      if (result.data.error) {
        NotificationManager.error('Something went wrong, Please try again');
        setLoader(false)
      } else {
        const quarterLabels = result.data.quarters || []
        // Quarter columns come after the static columns; current quarter first (as returned by API).
        const quarterCols = quarterLabels.map((label, i) => ({ name: `q${i}`, label }))
        setColumns([...baseColumns, ...quarterCols])
        setSnapshotDate(result.data.snapshotDate || null)

        const rows = (result.data.data || []).map(r => {
          const obj = {
            product: r.productName || '',
            // "Others" medicine type is the PCD category — display it as "PCD".
            medicineType: r.medicineType === 'Others' ? 'PCD' : (r.medicineType || ''),
            company: r.companyName || '',
            seller: r.sellerName || '',
            listingDate: r.listingDate ? moment(r.listingDate).format('DD MMM YYYY') : '',
            listingType: r.listingType || '',
            discount: r.discountText ? r.discountText : '-',
            listedQuantity: r.listedQuantity != null ? r.listedQuantity : 0,
            status: r.status || '',
          }
          ;(r.quarters || []).forEach((q, i) => {
            obj[`q${i}`] = q && q.sale != null ? Number(q.sale).toFixed(2) : '0.00'
          })
          return obj
        })
        setReport(rows)
        setCount(result.data.pagination ? result.data.pagination.totalCount : rows.length)
        setPage(pageArg)
        setLoader(false)
      }
    }).catch(error => {
      NotificationManager.error('Something went wrong, Please try again')
      setLoader(false)
    });
  }

  // Downloads the ENTIRE filtered dataset (ignores pagination) as CSV.
  const downloadReport = async () => {
    setDownloading(true)
    NotificationManager.info('Preparing full report, please wait…')
    await axios.post(`${AppConfig.baseUrl}reports/exportPromotionReport`,
      { seller_id: seller, search: searchedText },
      {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
          'token': localStorage.getItem('admin_token')
        }
      }
    ).then(result => {
      const url = window.URL.createObjectURL(new Blob([result.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `product-for-promotion-${sellerSlug}.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      setDownloading(false)
    }).catch(error => {
      NotificationManager.error('Could not download report, Please try again')
      setDownloading(false)
    })
  }

  const getSellerListing = async () => {
    await axios({
      method: 'get',
      url: `${AppConfig.baseUrl}inventory/get_filters`,
      headers: {
        'Content-Type': 'application/json',
        token: localStorage.getItem('admin_token')
      }
    }).then(result => {
      if (result.data.error) {
      } else {
        setFilterData(result.data.detail.sellers)
      }
    }).catch(error => {
      console.log("error catch", error)
    });
  }

  useEffect(() => {
    getSellerListing()
    getPromotionReport('', 0, '')
  }, [])

  // Filter / search changes always reset back to the first page.
  const applyFilter = async () => {
    getPromotionReport(seller, 0, searchedText);
  }

  const handleSearch = async (searchText) => {
    setSearchedText(searchText)
    getPromotionReport(seller, 0, searchText)
  }

  const handleChangePage = (newPage) => {
    getPromotionReport(seller, newPage, searchedText)
  }

  const handleChanged = (e) => {
    setSeller(e.target.value);
  }

  const options = {
    searchPlaceholder: 'Press Enter to Search',
    filterType: 'dropdown',
    responsive: 'scroll',
    print: false,
    download: true,
    // Override the built-in CSV (which only sees the current page) with a full-data server export.
    onDownload: () => {
      downloadReport()
      return false
    },
    selectableRows: false,
    selectableRowsOnClick: false,
    selectableRowsHeader: false,
    search: false,
    viewColumns: false,
    rowHover: false,
    filter: false,
    page: page,
    rowsPerPage: ROWS_PER_PAGE,
    rowsPerPageOptions: [],
    count: count,
    fixedHeader: false,
    sort: false,
    serverSide: true,
    searchText: '',
    onTableChange: (action, tableState) => {
      if (action === 'changePage') {
        handleChangePage(tableState.page)
      }
    },
    textLabels: {
      filter: {
        all: "",
        title: "FILTERS",
      },
    },
    customFooter: (count, page, rowsPerPage, changeRowsPerPage, changePage) => {
      return <div className="p-4 d-flex justify-content-end">
        <PaginationCount page={page} rowsPerPage={rowsPerPage} count={count} />
        <Pagination count={Math.ceil(count / rowsPerPage)} page={page + 1} onChange={(e, page) => { changePage(page - 1) }} color="primary" />
      </div>
    }
  }

  return (
    <AdminContainer>
      <div className={'title'}>
        Product for Promotion
        {snapshotDate ? <span style={{ fontSize: '13px', fontWeight: 400, marginLeft: 10 }}>(as of {moment(snapshotDate).format('DD MMM YYYY')})</span> : null}
        {downloading ? <span style={{ fontSize: '13px', fontWeight: 400, marginLeft: 10 }}>Preparing download…</span> : null}
      </div>
      <div style={{ padding: '0px 1px 0 1px', boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)' }}>
        {loader ? <div className="loader-view"
          style={{ height: props.width >= 1200 ? 'calc(100vh - 259px)' : 'calc(100vh - 238px)' }}>
          <CircularProgress />
        </div> : null}
        {
          loader ?
            null :
            <MUIDataTable
              title={
                <div className="header">
                  <div className="Row-header d-flex w-100" style={{ marginTop: '-9px', alignItems: 'center' }}>
                    <div style={{ marginRight: '20px', height: '45px', display: 'flex', alignItems: 'center' }}>
                      <SearchBar handleSearch={handleSearch} searchText={searchedText} placeholder="Search Product" />
                    </div>
                    <div style={{ marginRight: '20px', height: '45px', display: 'flex', alignItems: 'center', width: window.innerWidth > 800 ? '40%' : '96%' }}>
                      <FormControl style={{ width: '100%', margin: 0 }}>
                        <Input style={{ height: '45px' }} type="select" className="form-control" disableUnderline={true} name="seller" id="seller"
                          value={seller}
                          onChange={handleChanged}
                        >
                          <option selected={seller === ''} value={''}>Select Seller</option>
                          {
                            filterData && filterData.map((val) => {
                              return (<option selected={val._id === seller} value={val._id}>{val.company_name}</option>)
                            })
                          }
                        </Input>
                      </FormControl>
                    </div>
                    <div style={{ height: '45px', display: 'flex', alignItems: 'center' }}>
                      <Button style={{ height: '45px' }} onClick={() => applyFilter()} variant="contained" className='filterButton' color='primary'>Generate</Button>
                    </div>
                  </div>
                </div>
              }
              data={report ? report : []}
              columns={columns}
              options={options}
              style={{ borderRadius: '0px !important' }}
            />
        }
      </div>
    </AdminContainer>
  );
}

export default ProductForPromotion;
